// ProfileView.tsx
import React, { useState } from 'react';
import {
  MapPin,
  Heart,
  Share2,
  Eye,
  User,
  Zap,
  BookOpen,
  MessageCircle,
  CheckCircle,
  Star,
  Calendar,
  Clock,
  Activity,
  Ruler,
  Users,
  Briefcase,
  GraduationCap,
  HeartHandshake,
  Globe,
  Coffee,
  Music,
  Camera,
  Dumbbell,
  Award,
  Shield,
  Mail,
  Phone,
  Link as LinkIcon,
  Copy,
  Check,
  X,
  Flag
} from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/context/ToastContext';
import { FemaleIcon, MaleIcon } from './profileHeplers/GenderSVG';
import myIcons from '@/assets/Icons';

interface Profile {
  user_id: string;
  nickname: string;
  full_name?: string;
  age: number;
  gender: string;
  city: string;
  country: string;
  origin_state?: string;
  bio?: string;
  interests?: string[];
  height_cm?: number;
  body_build?: string;
  complexion?: string;
  hair_color?: string;
  eye_color?: string;
  ethnicity?: string;
  tribe?: string;
  education?: string;
  occupation?: string;
  religion?: string;
  marital_status?: string;
  seeking_status?: string;
  last_active_at?: number;
  created_at?: number;
  profile_views?: number;
  photos?: string[];
  is_active?: boolean;
  verified?: boolean;
  premium?: boolean;
  email?: string;
  phone?: string;
  website?: string;
  preferences?: {
    preferred_age_min?: number;
    preferred_age_max?: number;
    relocation?: string;
  };
  desired_qualities?: {
    qualities?: string[];
  };
  likes_count?: number;
  saves_count?: number;
  super_likes_count?: number;
  my_reaction_type?: string;
}

interface ProfileViewProps {
  profile: Profile;
  onClose?: () => void;
  onLike?: (profile: Profile) => void;
  onSuperLike?: (profile: Profile) => void;
  onSave?: (profile: Profile) => void;
  onMessage?: (profile: Profile) => void;
  onReport?: (profile: Profile) => void;
}

export default function ProfileView({ 
  profile, 
  onClose, 
  onLike, 
  onSuperLike, 
  onSave, 
  onMessage,
  onReport 
}: ProfileViewProps) {
  const toast = useToast();
  const [isLiked, setIsLiked] = useState(profile?.my_reaction_type === 'like');
  const [isSaved, setIsSaved] = useState(profile?.my_reaction_type === 'save');
  const [isSuperLiked, setIsSuperLiked] = useState(profile?.my_reaction_type === 'super_like');
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(profile?.photos?.[0] || null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const isMale = profile?.gender === "m" || profile?.gender === "male";
  const displayName = profile?.nickname || profile?.full_name || "Anonymous";
  const locationDisplay = profile?.city 
    ? (profile?.country ? `${profile?.city}, ${profile?.country}` : profile?.city)
    : (profile?.origin_state || "Location not specified");

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'N/A';
    return format(new Date(timestamp * 1000), 'PPP');
  };

  const formatLastActive = () => {
    if (!profile?.last_active_at) return 'Never';
    const lastActive = new Date(profile.last_active_at * 1000);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - lastActive.getTime()) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    return formatDate(profile.last_active_at);
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(`${field} copied to clipboard!`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
    onLike?.(profile);
    toast.success(isLiked ? 'Removed like' : `Liked ${displayName}`);
  };

  const handleSuperLike = () => {
    setIsSuperLiked(!isSuperLiked);
    onSuperLike?.(profile);
    toast.success(isSuperLiked ? 'Removed super like' : `Super liked ${displayName}! ✨`);
  };

  const handleSave = () => {
    setIsSaved(!isSaved);
    onSave?.(profile);
    toast.success(isSaved ? 'Removed from saved' : `Saved ${displayName} to favorites`);
  };

  const handleMessage = () => {
    onMessage?.(profile);
  };

  const handleReport = () => {
    onReport?.(profile);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/profile/${profile?.user_id}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${displayName}, ${profile?.age}`,
          text: `Check out ${displayName}'s profile`,
          url: url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Profile link copied!');
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  return (
    <div className="relative bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full mx-auto overflow-hidden">
      {/* Close Button */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      {/* Header with Cover Photo */}
      <div className="relative h-48 bg-gradient-to-r from-purple-500 to-pink-500">
        <div className="absolute -bottom-16 left-6">
          <div className="relative">
            <div className={`w-32 h-32 rounded-full border-4 border-white dark:border-gray-800 overflow-hidden bg-gradient-to-br ${
              isMale 
                ? "from-indigo-100 to-indigo-200 dark:from-indigo-900/30 dark:to-indigo-800/30" 
                : "from-pink-100 to-pink-200 dark:from-pink-900/30 dark:to-pink-800/30"
            }`}>
              {selectedPhoto ? (
                <img 
                  src={selectedPhoto} 
                  alt={displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                      {isMale ? (
                             <img src={myIcons.man} className='h-full object-cover' />
                           ) : (
                             <img src={myIcons.woman} className='h-full object-cover'/>
                           )}
                </div>
              )}
            </div>
            
            {/* Verification Badge */}
            {profile?.verified && (
              <div className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-1 border-2 border-white dark:border-gray-800">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-20 px-6 pb-6">
        {/* Basic Info */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {displayName}, {profile?.age}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">{locationDisplay}</span>
              </div>
              {profile?.is_active && (
                <div className="flex items-center gap-1 text-green-500">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-xs">Online</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Premium Badge */}
          {profile?.premium && (
            <div className="flex items-center gap-1 bg-gradient-to-r from-yellow-400 to-yellow-600 px-3 py-1 rounded-full">
              <Star className="w-4 h-4 text-white fill-white" />
              <span className="text-xs font-semibold text-white">Premium</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={handleLike}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              isLiked 
                ? 'bg-red-500 text-white' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-red-100 dark:hover:bg-red-900/30'
            }`}
          >
            <Heart className={`w-4 h-4 ${isLiked ? 'fill-white' : ''}`} />
            <span>{isLiked ? 'Liked' : 'Like'}</span>
          </button>

          <button
            onClick={handleSuperLike}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              isSuperLiked 
                ? 'bg-yellow-500 text-white' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
            }`}
          >
            <Zap className={`w-4 h-4 ${isSuperLiked ? 'fill-white' : ''}`} />
            <span>{isSuperLiked ? 'Super Liked' : 'Super Like'}</span>
          </button>

          <button
            onClick={handleSave}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              isSaved 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-900/30'
            }`}
          >
            <BookOpen className={`w-4 h-4 ${isSaved ? 'fill-white' : ''}`} />
            <span>{isSaved ? 'Saved' : 'Save'}</span>
          </button>

          <button
            onClick={handleMessage}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-all"
          >
            <MessageCircle className="w-4 h-4" />
            <span>Message</span>
          </button>

          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
          >
            <Share2 className="w-4 h-4" />
            <span>Share</span>
          </button>

          <button
            onClick={handleReport}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all"
          >
            <Flag className="w-4 h-4" />
            <span>Report</span>
          </button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {profile?.likes_count || 0}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Likes</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {profile?.saves_count || 0}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Saves</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {profile?.profile_views || 0}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Profile Views</div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* About Section */}
            {profile?.bio && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  About Me
                </h2>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {profile.bio}
                </p>
              </div>
            )}

            {/* Physical Attributes */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Physical Attributes
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {profile?.height_cm && (
                  <div className="flex items-center gap-2 text-sm">
                    <Ruler className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{profile.height_cm} cm</span>
                  </div>
                )}
                {profile?.body_build && (
                  <div className="flex items-center gap-2 text-sm">
                    <Activity className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300 capitalize">{profile.body_build}</span>
                  </div>
                )}
                {profile?.complexion && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300 capitalize">{profile.complexion} skin</span>
                  </div>
                )}
                {profile?.hair_color && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300 capitalize">{profile.hair_color} hair</span>
                  </div>
                )}
                {profile?.eye_color && (
                  <div className="flex items-center gap-2 text-sm">
                    <Eye className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300 capitalize">{profile.eye_color} eyes</span>
                  </div>
                )}
              </div>
            </div>

            {/* Interests */}
            {profile?.interests && profile.interests.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <HeartHandshake className="w-5 h-5" />
                  Interests
                </h2>
                <div className="flex flex-wrap gap-2">
                  {profile.interests.map((interest) => (
                    <span key={interest} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm">
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Personal Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <User className="w-5 h-5" />
                Personal Information
              </h2>
              <div className="space-y-2">
                {profile?.marital_status && (
                  <div className="flex items-center gap-2 text-sm">
                    <HeartHandshake className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300 capitalize">{profile.marital_status}</span>
                  </div>
                )}
                {profile?.ethnicity && (
                  <div className="flex items-center gap-2 text-sm">
                    <Globe className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{profile.ethnicity}</span>
                  </div>
                )}
                {profile?.tribe && (
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">Tribe: {profile.tribe}</span>
                  </div>
                )}
                {profile?.religion && (
                  <div className="flex items-center gap-2 text-sm">
                    <Shield className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{profile.religion}</span>
                  </div>
                )}
                {profile?.education && (
                  <div className="flex items-center gap-2 text-sm">
                    <GraduationCap className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{profile.education}</span>
                  </div>
                )}
                {profile?.occupation && (
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{profile.occupation}</span>
                  </div>
                )}
              </div>
            </div>

            {/* What I'm Looking For */}
            {(profile?.preferences || profile?.desired_qualities?.qualities) && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Heart className="w-5 h-5" />
                  What I'm Looking For
                </h2>
                <div className="space-y-2">
                  {profile?.preferences?.preferred_age_min && profile?.preferences?.preferred_age_max && (
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      Age: {profile.preferences.preferred_age_min} - {profile.preferences.preferred_age_max} years
                    </div>
                  )}
                  {profile?.preferences?.relocation && (
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      Relocation: {profile.preferences.relocation === 'Yes' ? 'Willing to relocate' : 'Not willing to relocate'}
                    </div>
                  )}
                  {profile?.desired_qualities?.qualities && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {profile.desired_qualities.qualities.map((quality) => (
                        <span key={quality} className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm">
                          {quality}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Contact Information (if available) */}
            {(profile?.email || profile?.phone || profile?.website) && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Contact Information
                </h2>
                <div className="space-y-2">
                  {profile?.email && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">{profile.email}</span>
                      </div>
                      <button
                        onClick={() => copyToClipboard(profile.email!, 'Email')}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      >
                        {copiedField === 'Email' ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  )}
                  {profile?.phone && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">{profile.phone}</span>
                      </div>
                      <button
                        onClick={() => copyToClipboard(profile.phone!, 'Phone')}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      >
                        {copiedField === 'Phone' ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  )}
                  {profile?.website && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <LinkIcon className="w-4 h-4 text-gray-400" />
                        <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-sm text-purple-600 dark:text-purple-400 hover:underline">
                          Website
                        </a>
                      </div>
                      <button
                        onClick={() => copyToClipboard(profile.website!, 'Website')}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      >
                        {copiedField === 'Website' ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Member Since */}
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
              <Calendar className="w-4 h-4" />
              <span>Member since {formatDate(profile?.created_at)}</span>
              <Clock className="w-4 h-4 ml-2" />
              <span>Last active {formatLastActive()}</span>
            </div>
          </div>
        </div>

        {/* Photo Gallery */}
        {profile?.photos && profile.photos.length > 1 && (
          <div className="mt-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Photos</h2>
            <div className="grid grid-cols-4 gap-2">
              {profile.photos.map((photo, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedPhoto(photo)}
                  className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                    selectedPhoto === photo 
                      ? 'border-purple-500' 
                      : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <img src={photo} alt={`${displayName} ${index + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}